// 메일 주소 → 사람 이름 사전.
//
// 왜 필요한가:
//   보낸메일함의 `to_addrs` 는 순수 주소 배열이라("a@b.com") 이름 정보가 없다.
//   받은메일의 from_addr 은 "홍길동 <a@b>" 형태로 이름이 같이 오는데, 보낸 쪽은
//   우리가 주소만 저장하기 때문이다. 그래서 조직도와 주소록에서 이름을 찾아 붙인다.
//
// 어디서 찾는가 (우선순위):
//   1. 조직도(/users)   — 사내 직원. 가장 정확하고 최신이다.
//   2. 주소록(/mail/contacts) — 외부 거래처. 사용자가 직접 고친 이름이 있으면 그게 맞다.
//
// 한 번만 불러 캐시한다. 목록 행마다 조회하면 화면이 스크롤될 때마다 요청이 쏟아진다.

import { useEffect, useState } from "react";
import { api } from "../../shared/api/client";

type NameMap = Record<string, string>;   // 소문자 이메일 -> 표시 이름

let cache: NameMap | null = null;
let inflight: Promise<NameMap> | null = null;
const listeners = new Set<(m: NameMap) => void>();

async function load(): Promise<NameMap> {
  if (cache) return cache;
  if (inflight) return inflight;

  inflight = (async () => {
    const map: NameMap = {};
    // 주소록을 먼저 깔고 조직도로 덮는다 — 사내 직원 이름이 우선이 되도록.
    try {
      const r = await api.get<{ id: number; name: string; email: string }[]>("/mail/contacts/");
      for (const c of Array.isArray(r.data) ? r.data : []) {
        if (c.email && c.name) map[c.email.trim().toLowerCase()] = c.name.trim();
      }
    } catch { /* 주소록이 없어도 조직도만으로 동작한다 */ }

    try {
      const r = await api.get<{ data: { name?: string; email?: string; username?: string }[] }>(
        "/users?size=500",
      );
      for (const u of r.data?.data || []) {
        const nm = (u.name || "").trim();
        if (!nm) continue;
        if (u.email) map[u.email.trim().toLowerCase()] = nm;
        // 사내 메일 주소(username@timple.me)도 같은 이름으로 잡아 준다.
        if (u.username) map[`${u.username.trim().toLowerCase()}@timple.me`] = nm;
      }
    } catch { /* 권한이 없으면 주소 그대로 보여 준다 */ }

    cache = map;
    inflight = null;
    listeners.forEach((fn) => fn(map));
    return map;
  })();

  return inflight;
}

/** 새 주소록 항목이 생겼을 때 다시 읽게 한다(주소록 화면에서 호출). */
export function invalidateAddressNames() {
  cache = null;
}

/**
 * 주소를 사람 이름으로 바꿔 주는 함수를 돌려준다.
 * 못 찾으면 주소의 아이디 부분을 쓴다(도메인까지 늘어놓으면 목록이 지저분해진다).
 */
export function useAddressNames() {
  const [map, setMap] = useState<NameMap>(cache || {});

  useEffect(() => {
    let alive = true;
    const onLoad = (m: NameMap) => { if (alive) setMap(m); };
    listeners.add(onLoad);
    void load().then(onLoad);
    return () => { alive = false; listeners.delete(onLoad); };
  }, []);

  /** "홍길동 <a@b>" 든 "a@b" 든 사람이 읽을 이름으로. */
  const nameOf = (raw: string): string => {
    if (!raw) return "";
    const withName = raw.match(/^\s*"?([^"<]+?)"?\s*<[^>]+>/);   // 이름이 이미 붙어 있으면 그대로
    if (withName && withName[1].trim()) return withName[1].trim();
    const email = (raw.match(/<([^>]+)>/)?.[1] || raw).trim().toLowerCase();
    return map[email] || email.split("@")[0] || email;
  };

  /** 여러 명을 "홍길동 외 2명" 으로 줄여 준다. 목록 한 줄에 다 넣을 수 없다. */
  const namesOf = (list: string[]): string => {
    if (!list || list.length === 0) return "";
    const first = nameOf(list[0]);
    return list.length > 1 ? `${first} 외 ${list.length - 1}명` : first;
  };

  return { nameOf, namesOf, ready: Object.keys(map).length > 0 };
}
